/**
 * Events that outlive the page that produced them.
 *
 * PostHog on the marketing site does not start until 4 s after load or the
 * first scroll (`afterBlockingWindow` in main.tsx; a tap deliberately does not
 * arm it, to keep the library off the interaction it would slow down). Until
 * then `track()` only fills an in-memory array, and an array dies with the
 * page.
 *
 * So a visitor who lands on the home page and clicks a hero link inside those
 * 4 s leaves no trace at all: no `$pageview`, no click. Measured 2026-09-19,
 * the median time on the home page is 6 s on mobile and 11 s on desktop, so
 * this is not an edge case — it is a real share of the people the hero works
 * best on, and the better the hero does, the more of them go missing.
 *
 * The fix keeps the 4 s rule. On `pagehide`, whatever is still buffered is
 * written to sessionStorage, and whichever page loads PostHog next replays it:
 * another marketing page, or the app (apps/app/src/lib/posthog.ts reads the
 * same key — same origin, same PostHog project, same person). Each event
 * carries the URL and time it actually happened, because the page replaying
 * it is a different one, later.
 *
 * Pure on purpose: storage and clock are passed in, so this is unit-tested
 * without a browser.
 */

/** Shared with apps/app/src/lib/posthog.ts. Change both or neither. */
export const HANDOFF_KEY = 'lc-ph-handoff';

/** A stale tab must not replay last week's click as today's. */
export const HANDOFF_MAX_AGE_MS = 30 * 60 * 1000;

/** Bounded: this rides on sessionStorage and should never grow past a visit. */
export const HANDOFF_MAX_EVENTS = 20;

export interface BufferedEvent {
  event: string;
  properties?: Record<string, unknown>;
  /** When it happened (ms epoch), not when it was replayed. */
  ts: number;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** What PostHog would have stamped had it been loaded, taken at event time:
 *  replayed from another page, the library would otherwise attribute the
 *  event to THAT page's URL. */
export function stampLocation(
  properties: Record<string, unknown> | undefined,
  loc: { href: string; pathname: string; host: string },
): Record<string, unknown> {
  return {
    $current_url: loc.href,
    $pathname: loc.pathname,
    $host: loc.host,
    ...properties,
  };
}

/** Append `events` to whatever is already parked. Never throws: private mode
 *  and blocked storage must cost the visitor nothing. */
export function parkEvents(storage: StorageLike | undefined, events: BufferedEvent[]): void {
  if (!storage || events.length === 0) return;
  try {
    const existing = readParked(storage, Number.MAX_SAFE_INTEGER, 0);
    const merged = [...existing, ...events].slice(-HANDOFF_MAX_EVENTS);
    storage.setItem(HANDOFF_KEY, JSON.stringify(merged));
  } catch {
    // Quota, private mode, disabled storage: drop silently.
  }
}

function readParked(storage: StorageLike, maxAgeMs: number, now: number): BufferedEvent[] {
  const raw = storage.getItem(HANDOFF_KEY);
  if (!raw) return [];
  const parsed: unknown = JSON.parse(raw);
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(
    (e): e is BufferedEvent =>
      !!e &&
      typeof (e as BufferedEvent).event === 'string' &&
      typeof (e as BufferedEvent).ts === 'number' &&
      now - (e as BufferedEvent).ts <= maxAgeMs,
  );
}

/** Take everything parked, and clear it so it is replayed exactly once. */
export function takeParked(storage: StorageLike | undefined, now: number): BufferedEvent[] {
  if (!storage) return [];
  try {
    const events = readParked(storage, HANDOFF_MAX_AGE_MS, now);
    storage.removeItem(HANDOFF_KEY);
    return events;
  } catch {
    try {
      storage.removeItem(HANDOFF_KEY);
    } catch {
      // Nothing left to do.
    }
    return [];
  }
}
